'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Building2,
  CheckCircle2,
  Send,
  Loader2,
  Sparkles,
  Users,
  Calendar,
  Phone,
  Mail,
  User,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { customPlanApi, getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { ALL_ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS } from '@/config/plans.config';

interface CustomPlanRequestModalProps {}

export function CustomPlanRequestModal({}: CustomPlanRequestModalProps) {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState<{ id?: string | number } | null>(null);

  // Form State
  const [eventTypes, setEventTypes] = useState<string[]>(['hackathon', 'conference']);
  const [expectedParticipants, setExpectedParticipants] = useState<string>('3500');
  const [estimatedEventsPerYear, setEstimatedEventsPerYear] = useState<string>('4');
  const [requirements, setRequirements] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState<string>(''); // Honeypot

  useEffect(() => {
    const handleCustomRequest = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
      if (detail.expected_participants) {
        setExpectedParticipants(String(detail.expected_participants));
      }
      if (detail.requirements) {
        setRequirements(String(detail.requirements));
      }
      // Populate defaults from auth store if available
      if (user) {
        setContactName(user.name || '');
        setContactEmail(user.email || '');
      }
      setSubmittedData(null);
      setIsOpen(true);
    };

    window.addEventListener('plan:custom-request', handleCustomRequest);
    return () => {
      window.removeEventListener('plan:custom-request', handleCustomRequest);
    };
  }, [user]);

  const toggleEventType = (typeKey: string) => {
    setEventTypes((prev) =>
      prev.includes(typeKey) ? prev.filter((t) => t !== typeKey) : [...prev, typeKey]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (websiteUrl) {
      // Silently treat as success without calling backend
      setSubmittedData({ id: 'CPR-BOT' });
      return;
    }

    if (!contactName.trim()) {
      toast.error('Please enter your contact name.');
      return;
    }

    if (!contactEmail.trim() || !contactEmail.includes('@')) {
      toast.error('Please enter a valid work email.');
      return;
    }

    if (eventTypes.length === 0) {
      toast.error('Please select at least one event type.');
      return;
    }

    if (requirements.trim().length < 20) {
      toast.error('Please describe your requirements (minimum 20 characters).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await customPlanApi.submitRequest({
        event_types: eventTypes,
        expected_participants: expectedParticipants ? parseInt(expectedParticipants, 10) : null,
        estimated_events_per_year: estimatedEventsPerYear
          ? parseInt(estimatedEventsPerYear, 10)
          : null,
        requirements: requirements.trim(),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim() || undefined,
        organization_name: organizationName.trim() || undefined,
      });

      const reqData = res.data?.data;
      setSubmittedData({ id: reqData?.id ?? reqData?.reference_id ?? 'PENDING' });
      toast.success('Custom plan request submitted!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to submit request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setIsOpen(false);
    setSubmittedData(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-stone-200 bg-white p-6 sm:p-8 text-slate-900 shadow-2xl my-8"
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={close}
            className="absolute right-5 top-5 rounded-full p-2 text-slate-400 hover:bg-stone-100 hover:text-slate-700 transition cursor-pointer"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>

          {submittedData ? (
            /* Success State */
            <div className="py-8 text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900">Custom Plan Request Received</h3>
              <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                Thank you, <strong className="text-slate-900">{contactName}</strong>. Our enterprise
                partnerships team has received your inquiry (Ref #{submittedData.id}) and will reach
                out to <span className="text-emerald-700 font-semibold">{contactEmail}</span> within 24
                business hours.
              </p>

              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 max-w-md mx-auto text-left text-xs text-slate-600 space-y-1.5">
                <p>
                  <strong className="text-slate-800">Expected scale:</strong>{' '}
                  {expectedParticipants ? `${Number(expectedParticipants).toLocaleString()} participants` : 'Large scale'}
                </p>
                <p>
                  <strong className="text-slate-800">Target event formats:</strong>{' '}
                  {eventTypes.map((t) => ACTIVITY_TYPE_LABELS[t] || t).join(', ')}
                </p>
              </div>

              <div className="pt-3">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-xl bg-[#1C3B2B] hover:bg-[#152e22] px-6 py-2.5 text-sm font-semibold text-white transition shadow-sm cursor-pointer"
                >
                  Return to App
                </button>
              </div>
            </div>
          ) : (
            /* Form State */
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                  <Building2 className="h-3.5 w-3.5 text-emerald-700" />
                  Enterprise & Custom Tier
                </span>
              </div>

              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                Request a Custom Plan
              </h2>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Host large-scale hackathons, conferences, and festivals beyond 3,000 participants
                with custom fee rates and dedicated support.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                {/* Honeypot field (hidden from real users) */}
                <div style={{ display: 'none' }} aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Event Types Multi-Select */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                      Primary Event Types <span className="text-rose-500">*</span>
                    </label>
                    <span className="text-[11px] text-stone-400 font-medium">Select all that apply</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ALL_ACTIVITY_TYPES.map((typeKey) => {
                      const selected = eventTypes.includes(typeKey);
                      return (
                        <button
                          key={typeKey}
                          type="button"
                          onClick={() => toggleEventType(typeKey)}
                          className={`rounded-lg px-3 py-1.5 text-xs transition border cursor-pointer ${
                            selected
                              ? 'bg-[#1C3B2B] border-[#1C3B2B] text-white font-semibold shadow-2xs'
                              : 'bg-stone-50 border-stone-200 text-slate-700 hover:bg-stone-100 hover:border-stone-300 font-medium'
                          }`}
                        >
                          {ACTIVITY_TYPE_LABELS[typeKey] || typeKey}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Scale Inputs Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Expected Participants / Event
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="number"
                        min="100"
                        step="100"
                        value={expectedParticipants}
                        onChange={(e) => setExpectedParticipants(e.target.value)}
                        placeholder="e.g. 5000"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Estimated Events / Year
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="number"
                        min="1"
                        value={estimatedEventsPerYear}
                        onChange={(e) => setEstimatedEventsPerYear(e.target.value)}
                        placeholder="e.g. 6"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Contact Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="text"
                        required
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="e.g. Alex Sharma"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Work Email <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="email"
                        required
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="alex@university.edu"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="tel"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                      Organization / University
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-3 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="text"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        placeholder="e.g. Stanford Tech Club"
                        className="w-full rounded-xl border border-stone-200 bg-stone-50/50 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Requirements Textarea */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                    Specific Requirements & Operational Needs <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    placeholder="Describe your expected scale, registration workflows, custom integrations, or sponsor billing requirements (min 20 characters)..."
                    className="w-full rounded-xl border border-stone-200 bg-stone-50/50 p-3.5 text-sm text-slate-900 placeholder:text-stone-400 focus:bg-white focus:border-[#1C3B2B] focus:ring-1 focus:ring-[#1C3B2B] outline-none transition"
                  />
                  <p className="text-xs text-stone-500 mt-1.5">
                    {requirements.trim().length < 20 ? (
                      <span className="text-amber-600 font-medium">
                        {20 - requirements.trim().length} more characters required
                      </span>
                    ) : (
                      <span className="text-emerald-700 font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Requirement details look good
                      </span>
                    )}
                  </p>
                </div>

                {/* Submit button */}
                <div className="pt-3 flex items-center justify-end gap-3 border-t border-stone-100">
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-stone-100 transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || requirements.trim().length < 20}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#1C3B2B] hover:bg-[#152e22] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting Inquiry…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
